import request from "./request";

// Upper bound on cached GET responses; the least recently used entry is
// evicted past this. Strides payloads can be large, so the cap bounds memory.
const MAX_CACHE_ENTRIES = 30;

// key → { data, promise }. A Map iterates in insertion order; entries are
// re-inserted on every read, so the first key is always the least recently
// used.
const cache = new Map();

// Stable cache key for a GET: url + params with null/undefined dropped and
// keys sorted, so equivalent requests built in different key orders match.
export const cacheKey = (url, params = {}) => {
  const clean = {};
  Object.keys(params)
    .filter((k) => params[k] != null)
    .sort()
    .forEach((k) => {
      clean[k] = params[k];
    });
  return `${url}?${JSON.stringify(clean)}`;
};

export const clearSwrCache = () => cache.clear();

// Stale-while-revalidate GET.
//   const { key, cached, fresh } = swrGet(url, { params })
// `cached` is the last payload seen for this key (undefined on first visit);
// `fresh` resolves with the network payload. Every call triggers a
// revalidation unless one is already in flight (concurrent callers share it).
// A failed fetch rejects `fresh` but never evicts previously cached data;
// a failed first fetch removes the entry so the next call retries.
//
// `silent: true` marks a speculative fetch — a prefetch the user may never
// need. It rides through into the axios config so the response interceptor in
// ./request can skip reporting it. It is deliberately NOT part of the cache
// key: the same URL prefetched and then requested for real is one entry.
export const swrGet = (url, { params, silent } = {}) => {
  const key = cacheKey(url, params);
  let entry = cache.get(key);
  if (entry) {
    // Re-insert to mark as most recently used.
    cache.delete(key);
    cache.set(key, entry);
  } else {
    entry = { data: undefined, promise: null };
    cache.set(key, entry);
    while (cache.size > MAX_CACHE_ENTRIES) {
      cache.delete(cache.keys().next().value);
    }
  }
  if (!entry.promise) {
    entry.promise = request
      .get(url, silent ? { params, silent } : { params })
      .then(({ data }) => {
        entry.data = data;
        entry.promise = null;
        return data;
      })
      .catch((error) => {
        entry.promise = null;
        if (entry.data === undefined && cache.get(key) === entry) {
          cache.delete(key);
        }
        throw error;
      });
  }
  return { key, cached: entry.data, fresh: entry.promise };
};
